import type { ChapterContent, FirstGameStep } from '../../types';

/**
 * Content lane — Nieświadomy Umysł + Koszmary (supreme quality pack).
 * Source: docs/perfect-quality/02_NOWE_GRY_PELNE_INSTRUKCJE/13_NIESWIADOMY_UMYSL_KOSZMARY_PELNA_INSTRUKCJA.md
 * Spec: docs/cursor/01_MASTER + docs/cursor/games/NIESWIADOMY_UMYSL_IMPLEMENTATION_PROMPT.md
 * ONE EXPANSION = ONE CHAPTER: Koszmary (requiredAddons: nightmares).
 * Pupile / Gramofon: verified procedures only; Gramofon remains DO WERYFIKACJI.
 * Catalogue blockers: ./blockers.ts — do not invent icons, cards, or scoring tables.
 * Stable First Game step ids (1–7) and chapter titles must be preserved for search/TTS/progress.
 */
export const nieswiadomyUmyslChapters: ChapterContent[] = [
  {
    title: '1. Cel gry i granice instrukcji',
    content: [
      {
        type: 'warning',
        text: 'Status treści: PARTIAL. Ten podręcznik uczy procedur systemu gry podstawowej i dodatku Koszmary z lokalnego szkicu Perfect Quality. Katalog konkretnych płytek notatnika, kart badań, pacjentów, marzeń, traum i ikon modułów nie jest tu przepisany — odczytujesz je z komponentów. Nie zgaduj tekstów kart ani wartości ikon.',
      },
      {
        type: 'heading',
        text: 'Co robisz i po co',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze są psychoanalitykami rozwijającymi techniki, publikującymi rozprawy i leczącymi pacjentów. Punkty zdobywa się podczas gry i w punktowaniu końcowym. Nie wygrywasz samym „zbieraniem kawy”: wygrywa ten, kto spójnie łączy stół spotkań, silnik notatnika, tarczę Wglądu, mapę Wiednia i terapię.',
      },
      {
        type: 'heading',
        text: 'Kiedy gra się kończy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy znacznik reputacji Freuda osiągnie końcowe pole, dokończ bieżącą rundę i rozegraj jeszcze jedną pełną rundę. Dopiero potem wykonaj punktowanie. Sygnał końca nie kasuje stołu w środku tury — wymusza dokończenie według procedury.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Freud wchodzi na pole końca w turze Anny. Anna dostaje wskazane punkty, odwraca znacznik pierwszego gracza, dokańcza swoją turę. Reszta rundy i jeszcze jedna pełna runda są obowiązkowe — dopiero potem punktujecie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: kończenie gry w chwili, gdy Freud „dotknie końca”, bez dokończenia rundy i bez dodatkowej pełnej rundy.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: przygotowanie — rozdziały 3–4; tura — rozdział 5; reputacja i koniec — rozdział 16; punktowanie — rozdział 20. Koszmary — jeden rozdział menu „Koszmary”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel: punkty z terapii, publikacji, silnika i reputacji. P: Czy ta instrukcja zastępuje teksty kart? O: Nie — katalogi są zablokowane. P: Czy Koszmary są w menu od razu? O: Tylko gdy włączysz dodatek; First Game zaleca najpierw podstawę.',
      },
    ],
  },
  {
    title: '2. Najważniejsze systemy',
    content: [
      {
        type: 'heading',
        text: 'Mapa decyzji przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gra krąży wokół kilku powiązanych systemów. Każdy ma własną procedurę; mieszanie ich „na oko” to najczęstsza przyczyna błędów.',
      },
      {
        type: 'step-list',
        steps: [
          'Stół spotkań i znaczniki Pomysłów — tu ogłaszasz i przywołujesz pracę.',
          'Plansza gracza z kałamarzem i płytkami notatnika — silnik efektów po Ogłoszeniu.',
          'Tarcza Wglądu — typ i poziom zasobów terapii oraz publikacji.',
          'Mapa Wiednia z profesorem i Freudem — lokacje i reputacja.',
          'Karty badań i rozprawy — budowa publikacji z tomów.',
          'Pacjenci, warstwy traumy i marzenia — terapia i Katharsis.',
          'Kawa, puzderka i świetne Pomysły — elastyczność i modyfikatory.',
          'Reputacja — tor Freuda i sygnał końca.',
          'W Koszmarach: Szaleństwo, spaczone tarcze, koszmarne marzenia, tajemniczy pacjenci, przeobrażone traumy, mroczne Wglądy; osobno pupile i gramofon.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład powiązań',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bartek Ogłasza Pomysły na pole badań, rozpatruje akcję, przesuwa kałamarz i uruchamia rząd notatnika. Dopiero potem, jeśli chce leczyć, czeka na osobną turę z opcją Lecz — nie łączy Ogłoszenia z terapią w jednej opcji tury.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie wszystkich systemów jak jednej „akcji uniwersalnej”. W turze wybierasz dokładnie jedną z trzech opcji.',
      },
      {
        type: 'tip',
        text: 'Diagram przepływu tury w aplikacji: Ogłoś → Akcja → Kałamarz → Uzupełnij gabinet (po opcjonalnych puzderkach).',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Główne systemy Nieświadomego Umysłu',
        fallbackText: 'Schemat: stół spotkań → kałamarz/notatnik → Wglądy → mapa → pacjenci; reputacja zamyka partię.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Systemy są osobne, ale zasilają te same punkty. P: Czy mapa to osobna opcja tury? O: Nie — efekty mapy wychodzą z Ogłoszenia, Przywołania albo innych efektów. P: Czy Koszmary zmieniają listę systemów? O: Tak — dokładają Szaleństwo i spaczenia; szczegóły w rozdziale Koszmary.',
      },
    ],
  },
  {
    title: '3. Przygotowanie wspólne',
    content: [
      {
        type: 'heading',
        text: 'Po co przygotowanie wspólne',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wspólny setup ustawia miasto, stół spotkań, pacjentów, marzenia i badania tak, by każda tura miała legalne cele. Bez poprawnych blokad pól i wystawek łatwo grać „nielegalnymi” Pomysłami albo pustymi gabinetami.',
      },
      {
        type: 'heading',
        text: 'Kolejność',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Rozłóż planszę miasta i stół spotkań.',
          'Przygotuj stosy dzielnic i umieść znaczniki reputacji na właściwych polach.',
          'Ustaw Freuda na lokacji wskazanej przez losowy cel.',
          'Rozłóż cele lokacji.',
          'Zablokuj pola Pomysłów odpowiednio dla 2 lub 3 graczy.',
          'Przygotuj osobne stosy płytek notatnika poziomu I i II oraz wystawki.',
          'Przygotuj pacjentów typowych i przełomowych.',
          'Na każdego widocznego pacjenta nałóż warstwę traumy.',
          'Przygotuj talie marzeń ukrytych i jawnych oraz wystawkę.',
          'Przygotuj karty badań, rozprawy i pulę świetnych pomysłów.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Liczba płytek notatnika według liczby graczy: 2 graczy — 12 poziomu I i 12 poziomu II; 3 graczy — 15 i 15; 4 graczy — 18 i 18.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przy trzech graczach blokujecie pola Pomysłów wskazane dla 3 osób i przygotujecie 15+15 płytek notatnika. Widoczni pacjenci dostają traumę zanim ktoś weźmie pierwszego pacjenta do gabinetu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zostawianie widocznych pacjentów bez warstwy traumy albo użycie liczby płytek z innej liczby graczy.',
      },
      {
        type: 'tip',
        text: 'Przy Koszmarach dodajesz komponenty z rozdziału Koszmary po (lub zamiast części) setupu podstawy — nie mieszaj bez listy z tego rozdziału.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wspólny setup = miasto + stół + wystawki + blokady. P: Czy Freud startuje zawsze w tej samej lokacji? O: Nie — według losowego celu. P: Czy 4 graczy blokują pola jak 2? O: Blokady są „odpowiednio dla 2 lub 3”; przy 4 stosuj regułę wydania.',
      },
    ],
  },
  {
    title: '4. Przygotowanie gracza',
    content: [
      {
        type: 'heading',
        text: 'Po co osobny setup gracza',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy gracz buduje własny gabinet, tarczę Wglądu i silnik notatnika. Bez poprawnego startu łatwo pomylić Wglądy kolorowe z rezerwą centralną albo otworzyć drugi gabinet za wcześnie.',
      },
      {
        type: 'heading',
        text: 'Co bierze każdy gracz',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Planszę gracza i gabinet.',
          'Tarczę Wglądu.',
          'Profesora.',
          'Kałamarz.',
          'Znaczniki Pomysłów.',
          'Znaczniki terapii.',
          'Znaczniki reputacji, punktów, kawy i puzderek.',
          'Wglądy zwykłe i w kolorze gracza.',
          'Startową warstwę traumy/pacjenta.',
          'Startowe płytki notatnika i zasoby zależne od kolejności.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Elementy ustawiasz zgodnie z oznaczeniami na planszach. Aplikacja nie zastępuje nadruków startowych ani dokładnych ilości zależnych od pozycji przy stole — odczytujesz je z komponentów.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Celina jako druga w kolejności bierze startowe płytki i zasoby wskazane dla tej pozycji, ustawia kałamarz i Wglądy według planszy. Nie kopiuje layoutu Anny „dla wygody” i nie otwiera drugiego gabinetu w setupie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pominięcie startowej traumy/pacjenta albo pomylenie Wglądów kolorowych z rezerwą centralną.',
      },
      {
        type: 'tip',
        text: 'Drugi gabinet odblokujesz później przez Katharsis pacjenta startowego — nie otwieraj go w setupie „na zapas”. Odsyłacz: nowy pacjent — rozdział 15.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Własny setup = plansza + tarcza + start według nadruków i kolejności. P: Czy wszyscy mają te same startowe zasoby? O: Zależą od kolejności. P: Czy kolorowe Wglądy od razu idą na mapę? O: Tylko po odblokowaniu i opłaceniu według reguł.',
      },
    ],
  },
  {
    title: '5. Struktura tury',
    content: [
      {
        type: 'heading',
        text: 'Co robisz w swojej turze',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze wykonują po jednej turze zgodnie z ruchem wskazówek zegara. W swojej turze wybierasz dokładnie jedną opcję: Ogłoś Pomysły, Przywołaj Pomysły albo Lecz Pacjentów.',
      },
      {
        type: 'heading',
        text: 'Po wybranej opcji',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Możesz użyć puzderek do terapii.',
          'Musisz uzupełnić każdy pusty gabinet nowym pacjentem i marzeniami.',
          'Usuń świetne Pomysły ze stołu, jeśli wymaga tego ich zasada.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład pełnej tury',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Darek wybiera Ogłoś. Umieszcza Pomysły, rozpatruje akcję, przesuwa kałamarz. Potem wydaje jedno puzderko na punkt terapii u pacjenta. Gabinet jest zajęty — nie dobiera nowego pacjenta. Świetny Pomysł na stole zostaje albo znika według własnej reguły.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wykonanie dwóch opcji tury (np. Ogłoś i Lecz) albo pominięcie obowiązkowego uzupełnienia pustego gabinetu.',
      },
      {
        type: 'tip',
        text: 'Kawa może być wydana w dowolnym momencie własnej tury, także w trakcie innego efektu — rozdział 7.',
      },
      {
        type: 'diagram',
        diagramId: 'procedure-flow',
        alt: 'Struktura tury',
        fallbackText: 'Schemat: wybierz jedną opcję → opcjonalne puzderka → uzupełnij puste gabinety → usuń świetne Pomysły jeśli trzeba.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jedna opcja na turę, potem sprzątanie gabinetów. P: Czy mogę wykonać dwie opcje? O: Nie. P: Czy puzderka są obowiązkowe? O: Nie — opcjonalne; uzupełnienie pustego gabinetu jest obowiązkowe.',
      },
    ],
  },
  {
    title: '6. Wglądy',
    content: [
      {
        type: 'heading',
        text: 'Co to jest Wgląd',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wgląd ma typ (rozwój, wolność albo pasja) oraz poziom (mały, średni albo duży). Tarcza ma trzy kolorowe sekcje i trzy okręgi. Centralna rezerwa przechowuje Wglądy bez typu i poziomu.',
      },
      {
        type: 'heading',
        text: 'Operacje na tarczy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Stworzenie — przenieś Wgląd z centralnej rezerwy na wskazany poziom i typ.',
          'Wzniesienie — przesuń o jeden okrąg na zewnątrz bez zmiany typu.',
          'Stłumienie — przesuń o jeden okrąg do wewnątrz; z małego poziomu można wrócić do rezerwy.',
          'Przesunięcie — przenieś do sąsiedniej sekcji bez zmiany poziomu.',
          'Użycie — przenieś wymagany Wgląd do centralnej rezerwy. Możesz nadpłacić wyższym poziomem, ale bez reszty.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Wglądy w kolorze gracza można umieszczać trwale na rozprawach i mapie po ich odblokowaniu.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ewa potrzebuje małego Wglądu rozwoju. Ma średni rozwój — używa go jako nadpłaty bez reszty (średni wraca do rezerwy, nie dostaje „zwrotu” małego). Nie może „dorzucić” stłumienia w tej samej płatności, żeby odzyskać różnicę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: oczekiwanie reszty przy nadpłacie wyższym poziomem albo przesuwanie Wglądu z rezerwy bez stworzenia.',
      },
      {
        type: 'tip',
        text: 'W Koszmarach spaczone miejsca i szalone Wglądy zmieniają te operacje — nie stosuj standardowego wzniesienia/stłumienia/przesunięcia do szalonego Wglądu.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Typ + poziom; pięć operacji; nadpłata bez reszty. P: Czy wyższy Wgląd może zastąpić niższy? O: Tak, bez reszty. P: Czy kolorowy Wgląd to to samo co zwykły na tarczy? O: Kolorowe służą do trwałych miejsc po odblokowaniu.',
      },
    ],
  },
  {
    title: '7. Kawa',
    content: [
      {
        type: 'heading',
        text: 'Po co kawa i kiedy jej używasz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kawy można używać w dowolnym momencie własnej tury, także podczas rozpatrywania innego efektu. To elastyczność Wglądów i świetnych Pomysłów, nie osobna opcja tury.',
      },
      {
        type: 'step-list',
        steps: [
          '1 kawa może zastąpić stłumienie Wglądu;',
          '2 kawy pozwalają wznieść albo przesunąć Wgląd;',
          '3 kawy dają świetny Pomysł.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Limit kawy wynosi 6. Nadwyżka przepada, ale przed otrzymaniem kawy możesz legalnie wydać posiadaną kawę.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Filip ma 5 kaw i efekt daje mu 2. Przed przyjęciem wydaje 1 kawę na stłumienie, przyjmuje 2 i kończy z limitem 6 zamiast tracić nadwyżkę.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: odkładanie kawy „na później poza turą” albo ignorowanie limitu 6 przy otrzymywaniu.',
      },
      {
        type: 'tip',
        text: 'Świetny Pomysł z 3 kaw wchodzi do puli jak inne świetne Pomysły — rozdział 8.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kawa = elastyczność w trakcie własnej tury; limit 6. P: Czy kawa może być wydana w trakcie efektu? O: Tak. P: Czy 3 kawy to od razu punkt? O: Nie — dają świetny Pomysł.',
      },
    ],
  },
  {
    title: '8. Świetne Pomysły',
    content: [
      {
        type: 'heading',
        text: 'Co mogą robić',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Świetne Pomysły są uniwersalnymi znacznikami i modyfikatorami. Mogą zastępować Pomysły gracza na stole spotkań, modyfikować ruch kałamarza, wzmacniać efekty lokacji albo służyć innym wskazanym efektom.',
      },
      {
        type: 'paragraph',
        text: 'Niewykorzystane świetne Pomysły dają po 1 punkcie na końcu gry. Gdy używasz ich na stole, usuń je w chwili wskazanej przez regułę.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gosia nie ma wolnego zwykłego Pomysłu na wybrane pole. Kładzie świetny Pomysł jako zastępstwo, rozpatruje akcję, a świetny usuwa w momencie wymaganym przez jego zasadę — nie zostawia go „na ozdobę” do końca rundy bez sprawdzenia tekstu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie świetnego Pomysłu jak zwykłego znacznika bez sprawdzenia, kiedy znika ze stołu.',
      },
      {
        type: 'tip',
        text: 'Przy Ogłoszeniu świetny może zastąpić zwykły Pomysł; przy kałamarzu może legalnie modyfikować ruch — rozdział 9.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Świetne Pomysły = elastyczność + 1 VP niewykorzystane. P: Czy zawsze zostają do scoringu? O: Nie — użyte według reguły znikają we wskazanej chwili. P: Czy zwiększają limit rozpatrzeń lokacji ponad limit? O: Mogą zwiększyć liczbę, ale nie ponad limit.',
      },
    ],
  },
  {
    title: '9. Ogłoś Pomysły',
    content: [
      {
        type: 'heading',
        text: 'Po co ta opcja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ogłoszenie to główny silnik: kładziesz Pomysły, bierzesz akcję pola i uruchamiasz kałamarz. First Game zaczyna od tej opcji u każdego gracza.',
      },
      {
        type: 'heading',
        text: '9.1 Umieść Pomysły',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Umieść 1 albo 2 Pomysły na jednym pustym polu stołu spotkań. Skieruj ich ogonki na jedno sąsiednie pole akcji. Nie możesz wskazać pola, na które wskazuje już inny twój Pomysł z wcześniejszej tury. Pomysły innych graczy nie blokują pola akcji. Świetny Pomysł może zastąpić zwykły Pomysł.',
      },
      {
        type: 'heading',
        text: '9.2 Rozpatrz akcję',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Za każdy umieszczony Pomysł rozpatrz wskazane pole raz. Jeśli wykonałeś akcję dwukrotnie, najpierw zakończ pierwsze rozpatrzenie. Pola obejmują m.in. pozyskiwanie płytek notatnika, karty badań, publikowanie, tworzenie i manipulowanie Wglądami, ruch profesorów i Freuda oraz inne efekty ikon — konkretne ikony odczytuj z komponentów.',
      },
      {
        type: 'heading',
        text: '9.3 Aktywuj kałamarz',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Policz symbole kałamarza w rzędzie, w którym umieściłeś Pomysły.',
          'Przesuń kałamarz dokładnie o tę wartość zgodnie z ruchem wskazówek zegara.',
          'Możesz użyć świetnych Pomysłów do legalnej modyfikacji.',
          'Rozpatrz kolumnę albo rząd płytek wskazany przez pole zatrzymania.',
          'Wykonuj efekty w wymaganej kolejności.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Nie pomijaj ruchu kałamarza.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Hania kładzie dwa Pomysły na jednym polu; oba ogonki wskazują to samo sąsiednie pole akcji (w podstawie nie wolno dwóch różnych pól). Rozpatruje akcję dwa razy po kolei, liczy symbole kałamarza w rzędzie, przesuwa kałamarz i uruchamia wskazaną linię notatnika.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijanie ruchu kałamarza; umieszczanie Pomysłów na dwóch polach w podstawie; kierowanie nowego Pomysłu na pole już wskazane przez własny Pomysł.',
      },
      {
        type: 'tip',
        text: 'Wyjątek Koszmarów: od poziomu 5 Szaleństwa dwa Pomysły mogą wskazywać dwa różne pola — tylko z włączonym dodatkiem i kartą podsumowania.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Umieść → akcja → kałamarz. P: Czy dwa Pomysły muszą wskazywać to samo pole? O: W podstawie tak. P: Czy mogę pominąć kałamarz, gdy nie mam płytek? O: Nie — ruch jest obowiązkowy.',
      },
    ],
  },
  {
    title: '10. Płytki notatnika',
    content: [
      {
        type: 'heading',
        text: 'Co robi płytka i po co',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Płytki notatnika mają akcję główną i baner. Budują silnik: rzędy i kolumny uruchamiane kałamarzem po Ogłoszeniu Pomysłów. Wypełnione rzędy punktują na końcu gry — nie w chwili położenia płytki.',
      },
      {
        type: 'heading',
        text: 'Kiedy i jak zdobywasz płytkę',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Zdobycie płytki wychodzi z akcji stołu spotkań albo innego efektu wskazującego pozyskanie. Procedura zawsze jest ta sama, niezależnie od źródła.',
      },
      {
        type: 'step-list',
        steps: [
          'Zapłać wymagany koszt kawy lub efektu (ikona na płytce / efekcie).',
          'Umieść płytkę w legalnym pustym miejscu na swojej planszy.',
          'Rozpatrz stronę albo efekt zgodnie z ikoną — odczyt z komponentu, nie z pamięci.',
          'Uzupełnij wystawkę płytek.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Igor rozpatruje akcję pozyskania płytek. Bierze płytkę poziomu I, płaci koszt z jej ikony, kładzie ją w pustym legalnym miejscu, rozpatruje efekt strony i uzupełnia wystawkę. W tej samej turze, po ruchu kałamarza, jeśli znacznik zatrzyma się na rzędzie lub kolumnie z tą płytką, uruchamia efekty linii w wymaganej kolejności — nie pomija ruchu kałamarza.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: odczytywanie efektu płytki z pamięci zamiast z ikony; pomijanie uzupełnienia wystawki; punktowanie wypełnionego rzędu od razu zamiast na końcu gry.',
      },
      {
        type: 'tip',
        text: 'Blocker: katalog konkretnych płytek notatnika wymaga osobnego przepisania ikon — tu tylko procedura. Powiązanie: Ogłoś i kałamarz — rozdział 9; scoring rzędów — rozdział 20.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Płytka = koszt → miejsce → efekt → wystawka; silnik = kałamarz. P: Czy wypełniony rząd daje punkty od razu? O: Nie — na końcu gry. P: Czy mogę położyć płytkę bez legalnego miejsca? O: Nie.',
      },
    ],
  },
  {
    title: '11. Karty badań i rozprawy',
    content: [
      {
        type: 'heading',
        text: 'Po co badania i rozprawy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karty badań dają symbole lokacji i efekty w grze. Rozprawy to publikacje budowane z tomów — źródło punktów i interakcji przez cytowanie. Teksty konkretnych kart odczytujesz z komponentów; tu uczysz procedury.',
      },
      {
        type: 'heading',
        text: 'Zdobywanie i zagrywanie',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Dobierz kartę z wystawki albo talii zgodnie z rozpatrywaną akcją.',
          'Zagraj dozwoloną liczbę kart (limit wynika z efektu / ikony, nie z domysłu).',
          'Rozpatrz symbole lokacji i efekty karty.',
          'Pamiętaj: do publikacji pierwszy tom musi pochodzić z własnej karty badań już w grze — nie z ręki.',
        ],
      },
      {
        type: 'heading',
        text: 'Publikacja rozprawy',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz dostępną rozprawę.',
          'Pierwszy wymagany tom musi pochodzić z własnej karty badań w grze.',
          'Pozostałe tomy mogą pochodzić z własnych kart albo z cytowania rozpraw innych graczy.',
          'Nie cytuj własnej rozprawy.',
          'Przy cytowaniu zacznij od prawej strony i nie zostawiaj przerw.',
          'Autor cytowanej rozprawy otrzymuje 2 punkty i świetny Pomysł za każdy cytowany tom.',
          'Umieść własny odblokowany Wgląd w kolorze gracza na rozprawie.',
          'Zdobądź punkty rozprawy.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Julia ma kartę badań już w grze (nie w ręce) jako pierwszy tom. Cytuje dwa tomy z prawej strony rozprawy Marka bez przerw. Marek dostaje 2 punkty i świetny Pomysł za każdy cytowany tom. Julia kładzie odblokowany kolorowy Wgląd i bierze punkty rozprawy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: używanie karty badań z ręki do publikacji; cytowanie własnej rozprawy; zostawianie przerw przy cytowaniu.',
      },
      {
        type: 'tip',
        text: 'Blocker: katalog kart badań i rozpraw — osobna weryfikacja ikon/tekstów.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pierwszy tom z własnej karty w grze; cytowanie od prawej bez przerw. P: Czy mogę cytować siebie? O: Nie. P: Co dostaje autor cytowanej rozprawy? O: 2 punkty i świetny Pomysł za każdy cytowany tom.',
      },
    ],
  },
  {
    title: '12. Mapa miasta',
    content: [
      {
        type: 'heading',
        text: 'Geografia Wiednia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Mapa ma 3 dzielnice, każda po 2 lokacje: Teatr, Biblioteka, Opera, Muzeum, Uniwersytet, Kawiarnia. Ruch profesora albo Freuda odbywa się zgodnie z ruchem wskazówek zegara, chyba że efekt mówi inaczej (w Koszmarach od progu 3 Szaleństwa profesor może iść przeciwnie — karta podsumowania).',
      },
      {
        type: 'heading',
        text: 'Kiedy rozpatrujesz lokację',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po wejściu do lokacji rozpatrz jej efekt. Liczbę rozpatrzeń bierzesz z jednej z dwóch metod wskazanych przez zasady i komponenty: z liczby figurek na polu albo z liczby właściwych symboli lokacji posiadanych w grze — zależnie od wybranej metody i limitu. Świetne Pomysły mogą zwiększyć liczbę rozpatrzeń, ale nie ponad limit.',
      },
      {
        type: 'warning',
        text: '[DO WERYFIKACJI / blocker] Pełna legenda ikon lokacji, dokładne limity i moment wyboru metody — z planszy i instrukcji wydania. Nie zgaduj wartości pól.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Krzysztof wchodzi profesorem do Biblioteki. Wybiera legalną metodę liczenia rozpatrzeń według komponentów, nie przekracza limitu i nie dodaje świetnych Pomysłów ponad ten limit. Gdy Freud przesuwa się tylko przez tor reputacji, Krzysztof nie odpala efektu lokacji „przy okazji”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zakładanie, że Freud przy ruchu tylko przez tor reputacji uruchamia efekty lokacji — nie, jeśli jest przesuwany tylko przez przekroczenie symbolu na torze.',
      },
      {
        type: 'tip',
        text: 'Przywołanie łączy się z premiami mapy — rozdział 13. Cele lokacji — rozdział 16. Katalog premii pól: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: '6 lokacji, ruch co do zasady zgodnie z zegarem, limit rozpatrzeń. P: Czy Freud zawsze odpala lokację? O: Nie przy samym przesunięciu z toru reputacji. P: Czy świetny Pomysł zdejmuje limit? O: Nie.',
      },
    ],
  },
  {
    title: '13. Przywołaj Pomysły',
    content: [
      {
        type: 'heading',
        text: 'Po co przywołujesz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ta opcja odzyskuje Pomysły ze stołu i aktywuje premie mapy. To nie jest drugie Ogłoszenie — nie kładziesz nowych Pomysłów na stół w tej opcji i nie uruchamiasz kałamarza.',
      },
      {
        type: 'heading',
        text: 'Kiedy stosujesz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wybierasz Przywołaj, gdy chcesz odzyskać własne Pomysły z pól stołu spotkań i skorzystać z premii powiązanej z pozycją profesora na mapie. W turze to jedna z trzech wyłącznych opcji.',
      },
      {
        type: 'heading',
        text: 'Procedura (zweryfikowany szkielet)',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz grupę własnych Pomysłów do przywołania zgodnie z zasadą (nie Pomysłów przeciwnika).',
          'Przenieś je do puli.',
          'Rozpatrz premię dzielnicy/lokacji powiązaną z profesorem.',
          'Umieść odblokowany Wgląd w kolorze gracza, jeśli płacisz wymagany koszt.',
          'Wykonaj efekty przywołania i aktualizuj mapę.',
        ],
      },
      {
        type: 'warning',
        text: '[DO WERYFIKACJI] Dokładna kolejność kroków szczegółowych i pełny katalog premii pól mapy — z polskiej instrukcji i legendy ikon. Nie uzupełniaj bonusów „na logikę”.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Laura przywołuje grupę własnych Pomysłów, wraca nimi do puli, bierze premię powiązaną z pozycją profesora i — jeśli opłaca koszt — kładzie odblokowany kolorowy Wgląd. Nie rozpatruje przy tym akcji stołu jak przy Ogłoszeniu i nie przesuwa kałamarza.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: mylenie Przywołania z Ogłoszeniem; pomijanie premii mapy po odzyskaniu Pomysłów; przywoływanie Pomysłów przeciwnika.',
      },
      {
        type: 'tip',
        text: 'W Koszmarach od 13 Szaleństwa przy przywołaniu zwracasz 6 Szaleństwa — według karty podsumowania. Powiązania: mapa (12), Ogłoś (9), Koszmary.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przywołaj = odzysk + premia mapy. P: Czy przywołanie uruchamia kałamarz? O: Nie — kałamarz jest w Ogłoszeniu. P: Czy mogę przywołać Pomysły przeciwnika? O: Nie — tylko własne zgodnie z zasadą.',
      },
    ],
  },
  {
    title: '14. Lecz Pacjentów',
    content: [
      {
        type: 'heading',
        text: 'Po co leczysz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Leczenie to droga przez marzenie, punkty terapii, Katharsis i wyleczenie. Musisz wykorzystać maksymalną możliwą liczbę punktów terapii na pacjenta.',
      },
      {
        type: 'heading',
        text: 'Procedura',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Użyj Wglądów i ewentualnie kawy wymaganych przez marzenie.',
          'Stwórz wskazaną liczbę punktów terapii.',
          'Przesuwaj znacznik terapii pacjenta w lewo.',
          'Rozpatruj każde mijane pole.',
          'Gdy przekroczysz Katharsis, zdobądź wskazane punkty, usuń warstwę traumy i aktywuj stałą część pacjenta.',
          'Gdy dojdziesz do końca, pacjent jest wyleczony i daje punkty.',
          'Odrzuć wykorzystane marzenie.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Dopiero nadmiar po wyleczeniu zamienia się w puzderka zgodnie z regułą. Pasujący symbol snu i traumy może dać dodatkowe puzderko.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marek płaci Wglądy z marzenia, tworzy 4 punkty terapii i przesuwa znacznik, mijając pola po kolei. Przy Katharsis usuwa traumę i aktywuje stały tekst pacjenta — punktów wyleczenia jeszcze nie bierze. Gdy znacznik dojdzie do końca, bierze punkty wyleczenia. Nie zostawia niewykorzystanych punktów „na później”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: zatrzymywanie niewykorzystanych punktów terapii; zdobywanie punktów pacjenta przy Katharsis zamiast przy wyleczeniu; dobrowolne niewykorzystanie części terapii.',
      },
      {
        type: 'tip',
        text: 'Koszmarne marzenia wymagają szalonego Wglądu i tworzą punkt terapii Katharsis — rozdział Koszmary. Katalog marzeń/pacjentów zablokowany.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Marzenie → terapia max → Katharsis → wyleczenie. P: Czy mogę dobrowolnie nie wykorzystać części terapii? O: Nie. P: Kiedy dobieram pacjenta? O: Na końcu własnej tury, jeśli gabinet jest pusty.',
      },
    ],
  },
  {
    title: '15. Puzderka i nowy pacjent',
    content: [
      {
        type: 'heading',
        text: 'Puzderka',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na końcu tury możesz wydawać puzderka po jednym, aby tworzyć punkty terapii dla dowolnych swoich pacjentów. Każdy punkt rozpatruj osobno. W dowolnym momencie możesz też użyć zestawu małego, średniego i dużego Wglądu dowolnych typów, aby otrzymać puzderko.',
      },
      {
        type: 'heading',
        text: 'Nowy pacjent (pusty gabinet)',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz widocznego pacjenta.',
          'Uzupełnij wystawkę i dodaj nową warstwę traumy.',
          'Dobierz ukryte marzenie.',
          'Wybierz jawne marzenie z wystawki albo talii.',
          'Ustaw znacznik terapii na sumie wymaganej przez pacjenta i traumę.',
          'Zdobądź punkty startowego pola, jeśli je ma.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Drugi gabinet odblokowujesz przez Katharsis pacjenta startowego.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nina kończy Leczenie z pustym gabinetem. Wydaje jedno puzderko na drugiego pacjenta (jeśli ma), potem obowiązkowo bierze nowego widocznego pacjenta z traumą i dwoma marzeniami. Nie traktuje niewydanego puzderka jak już aktywnego punktu terapii.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie puzderka jak punktu terapii przed wydaniem; pomijanie uzupełnienia pustego gabinetu.',
      },
      {
        type: 'tip',
        text: 'Puzderka są po wybranej opcji tury; nowy pacjent jest obowiązkowy dla każdego pustego gabinetu.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Puzderko wydajesz; pusty gabinet uzupełniasz. P: Czy puzderko = terapia bez wydania? O: Nie. P: Skąd drugi gabinet? O: Z Katharsis pacjenta startowego.',
      },
    ],
  },
  {
    title: '16. Cele lokacji i reputacja',
    content: [
      {
        type: 'heading',
        text: 'Cele lokacji',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy po raz pierwszy spełnisz warunek dostępnego celu: natychmiast go zdobądź, zyskaj punkt reputacji, odłóż kafel zakryty do swojej strefy i zachowaj jego symbole lokacji do końca gry. Przykładowe warunki: dwa wypełnione rzędy/kolumny, odblokowane Pomysły, opublikowane rozprawy, Wglądy na mapie, określona liczba wyleczonych pacjentów.',
      },
      {
        type: 'heading',
        text: 'Reputacja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy zdobywasz reputację: przesuń Freuda, przesuń własny znacznik, zastosuj pola i sygnał końca. Rozpatrzenie toru reputacji daje punkty/kawę i wybraną akcję z obszaru, ale samo nie przesuwa znaczników.',
      },
      {
        type: 'heading',
        text: 'Sygnał końca',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Aktywny gracz dostaje wskazane punkty.',
          'Odwróć znacznik pierwszego gracza.',
          'Dokończ rundę.',
          'Rozegraj jeszcze jedną rundę.',
          'Przejdź do punktowania.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ola spełnia cel dwóch wypełnionych rzędów: bierze cel, +1 reputacji, przesuwa Freuda i swój znacznik. Jeśli Freud wszedł na koniec, odpala procedurę dokończenia — nie punktuje od razu całej gry.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: mylenie rozpatrzenia toru reputacji (punkty/kawa/akcja bez przesuwania znaczników) z zdobyciem reputacji (przesunięcie Freuda i własnego znacznika).',
      },
      {
        type: 'tip',
        text: 'W Koszmarach żeton sprawdzenia Szaleństwa stoi na właściwym polu reputacji — patrz rozdział Koszmary.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel → reputacja → Freud; koniec = dokończ + jeszcze jedna runda. P: Czy rozpatrzenie toru przesuwa Freuda? O: Nie samo z siebie. P: Czy cel można wziąć ponownie? O: Warunek spełniasz po raz pierwszy dla dostępnego celu.',
      },
    ],
  },
  {
    title: 'Koszmary',
    requiredAddons: ['nightmares'],
    content: [
      {
        type: 'warning',
        text: 'Status dodatku: PARTIAL. Poniżej procedury systemowe Koszmarów z lokalnego szkicu. Katalog tajemniczych pacjentów, koszmarnych marzeń, przeobrażonych traum, mrocznych Wglądów i konkretnych tekstów kart pozostaje zablokowany — odczytuj z komponentów. Stosuj dokładny tekst karty podsumowania Szaleństwa.',
      },
      {
        type: 'heading',
        text: 'Co dodaje Koszmary i po co',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koszmary dokładają Szaleństwo, spaczone tarcze, koszmarne marzenia, tajemniczych pacjentów, przeobrażone traumy i mroczne Wglądy. Zwiększają elastyczność (progi Szaleństwa), ale karzą w sprawdzeniach. First Game: dodaj dopiero po zrozumieniu terapii i tarczy Wglądu.',
      },

      {
        type: 'heading',
        text: 'Przygotowanie dodatku',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Dodaj tajemniczych pacjentów do odpowiednich połówek talii.',
          'Dodaj koszmarne marzenia i osobną wystawkę.',
          'Wmieszaj przeobrażone warstwy traumy do części talii.',
          'Dodaj uniwersalne karty badań.',
          'Rozłóż rozprawę o zmarłych.',
          'Dodaj cel nawiedzonej lokacji.',
          'Przygotuj żetony Szaleństwa i mroczne Wglądy.',
          'Użyj spaczonych tarcz.',
          'Rozdaj szalone startowe płytki notatnika.',
          'Ustaw żeton sprawdzenia Szaleństwa na właściwym polu reputacji.',
        ],
      },
      {
        type: 'warning',
        text: 'Typowy błąd: włączenie Koszmarów bez spaczonych tarcz albo bez żetonu sprawdzenia na torze reputacji.',
      },

      {
        type: 'heading',
        text: 'Szaleństwo — poziomy i korzyści',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gdy otrzymujesz Szaleństwo, weź żeton. Pula jest nieograniczona. Poziomy odblokowują korzystne efekty (stosuj kartę podsumowania): od 3 — ruch profesora może odbywać się przeciwnie do wskazówek; od 5 — dwa Pomysły mogą wskazywać dwa różne pola; od 8 — dodatkowa elastyczność średnich Wglądów; od 13 — przy przywołaniu zwróć 6 Szaleństwa.',
      },
      {
        type: 'heading',
        text: 'Sprawdzenie Szaleństwa',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Uruchamia się: po przekroczeniu właściwego pola przez Freuda; gdy pierwszy gracz osiągnie co najmniej 13 i zdobędzie nawiedzony cel; przed punktacją końcową.',
      },
      {
        type: 'step-list',
        steps: [
          'Znajdź najniższy poziom Szaleństwa.',
          'Wszyscy usuwają tyle Szaleństwa.',
          'Każdy traci punkty równe pozostałemu Szaleństwu.',
          'W szalonym wariancie kara jest podwojona.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Przykład: najniższy poziom przy stole to 4. Wszyscy odrzucają po 4 Szaleństwa. Anna miała 7 — traci 3 punkty (w szalonym wariancie 6). Nie pomijaj sprawdzenia przed scoringiem.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie Szaleństwa wyłącznie jako kary albo pomijanie końcowego sprawdzenia przed punktacją.',
      },

      {
        type: 'heading',
        text: 'Spaczona tarcza i szalone Wglądy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy typ ma małe, średnie i duże spaczenie: małe — pojemność 1, bez Szaleństwa; średnie — pojemność 2, +1 Szaleństwa; duże — pojemność 3, +2 Szaleństwa. Wgląd w spaczeniu jest szalony. Nie można go standardowo wznieść, stłumić ani przesunąć. Po użyciu wraca do centralnej rezerwy. Szalony Wgląd może nadpłacić dowolny poziom tego samego typu.',
      },
      {
        type: 'paragraph',
        text: 'Przykład: Ola wkłada Wgląd do średniego spaczenia — bierze 1 Szaleństwo, slot ma pojemność 2. Nie może potem „wznieść” tego Wglądu jak zwykłego. Gdy użyje go do marzenia, wraca do rezerwy centralnej.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: poruszanie szalonego Wglądu standardowymi efektami wzniesienia, stłumienia lub przesunięcia.',
      },

      {
        type: 'heading',
        text: 'Mroczne Wglądy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Działają jak zwykłe, ale umieszczenie ich w spaczeniu nie daje Szaleństwa. Zdobywa się je głównie przez Katharsis tajemniczych pacjentów.',
      },

      {
        type: 'heading',
        text: 'Koszmarne marzenia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koszmarne marzenie wymaga co najmniej jednego szalonego Wglądu. Tworzy punkt terapii Katharsis: jeśli znacznik jest za polem Katharsis, przesuń go bezpośrednio do Katharsis i rozpatrz je; w innym wypadku użyj punktu jak zwykłej terapii.',
      },
      {
        type: 'paragraph',
        text: 'Przykład: znacznik Ewy jest za Katharsis. Płaci koszmarne marzenie z wymaganym szalonym Wglądem, przesuwa znacznik wprost do Katharsis, rozpatruje Katharsis (usuwa traumę, aktywuje stałą część) — nie traktuje tego jak zwykłego kroku o jedno pole wstecz „na oko”.',
      },

      {
        type: 'heading',
        text: 'Tajemniczy pacjenci i przeobrażone traumy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tajemniczy pacjenci dają specjalne efekty Katharsis i punktowanie. Przeobrażona trauma kieruje pacjenta ku Koszmarom. Teksty indywidualne muszą być w katalogu kart — tu nie są wymyślane.',
      },
      {
        type: 'tip',
        text: 'Blocker: katalog tajemniczych pacjentów, koszmarnych marzeń i przeobrażonych traum. Pupile i gramofon to osobne moduły (mogą iść z podstawą lub Koszmarami) — rozdziały Pupile i Gramofon; nie są dodatkowymi pozycjami menu Koszmarów.',
      },

      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koszmary = Szaleństwo + spaczenia + koszmarna terapia. P: Czy Szaleństwo jest wyłącznie karą? O: Nie — odblokowuje korzyści, ale boli w sprawdzeniach. P: Czy dwa Pomysły mogą wskazywać różne pola? O: Tak od progu 5 Szaleństwa według karty podsumowania. P: Czy szalony Wgląd wolno wznieść? O: Nie standardowymi operacjami.',
      },
    ],
  },
  {
    title: '18. Pupile',
    content: [
      {
        type: 'heading',
        text: 'Co to za moduł',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pupile to osobny moduł — można go stosować z podstawą albo z Koszmarami. Nie jest to ten sam rozdział menu co Koszmary.',
      },
      {
        type: 'step-list',
        steps: [
          'Pupil ma tor czterech poziomów.',
          'Podczas ustalania ruchu kałamarza możesz przekazać pupilowi 1–2 punkty ruchu.',
          'Kałamarz musi poruszyć się co najmniej o 1.',
          'Po wejściu pupila na pole wykonaj efekt.',
          'Strona kafla zależy od użycia Koszmarów.',
          'Id w solo nie używa pupila.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Piotr liczy 3 symbole kałamarza. Przekazuje pupilowi 2 punkty ruchu, więc kałamarz przesuwa się o 1. Pupil wchodzi na swoje pole i Piotr rozpatruje efekt strony kafla zgodnej z tym, czy gra z Koszmarami.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: oddanie całego ruchu pupilowi tak, że kałamarz stoi w miejscu — kałamarz musi ruszyć się co najmniej o 1.',
      },
      {
        type: 'tip',
        text: 'Blocker: pełne teksty pól toru pupila — z komponentu / katalogu ikon.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pupil dzieli ruch z kałamarzem; kałamarz ≥ 1. P: Czy pupil wymaga Koszmarów? O: Nie — działa też z podstawą. P: Czy Id w solo używa pupila? O: Nie.',
      },
    ],
  },
  {
    title: '19. Gramofon',
    content: [
      {
        type: 'warning',
        text: 'Status modułu: BLOCKED / DO WERYFIKACJI. Pełna sekwencja Gramofonu z polskiej instrukcji dodatku nie jest w lokalnym szkicu. Nie wymyślaj kroków, efektów lokacji ani timingów.',
      },
      {
        type: 'heading',
        text: 'Co wiadomo proceduralnie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gramofon jest osobnym modułem — nie częścią menu Koszmary. Zmienia wybraną lokalizację i sposób korzystania z efektów. Można go stosować z podstawą albo z Koszmarami, ale bez zweryfikowanej sekwencji nie uczcie go jako kompletnej procedury.',
      },
      {
        type: 'heading',
        text: 'Czego brakuje do domknięcia',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Pełna kolejność setupu modułu.',
          'Dokładny sposób zmiany wybranej lokalizacji.',
          'Timing korzystania z efektów względem tury.',
          'Wszystkie wyjątki względem mapy i świetnych Pomysłów.',
        ],
      },
      {
        type: 'tip',
        text: 'Materiał zamykający blocker: polska instrukcja dodatku / modułu Gramofon. Do tego czasu odczytuj wyłącznie z oficjalnego źródła przy stole.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gramofon = osobny moduł lokalizacji; procedura w aplikacji niepełna. P: Czy aplikacja ma pełną procedurę? O: Nie — status BLOCKED / DO WERYFIKACJI. P: Czy wolno zgadywać efekty? O: Nie. P: Czy Gramofon to pozycja menu Koszmary? O: Nie.',
      },
    ],
  },
  {
    title: '20. Punktowanie końcowe',
    content: [
      {
        type: 'heading',
        text: 'Kiedy punktujesz',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po ostatniej rundzie (po sygnale Freuda: dokończenie + jeszcze jedna pełna runda) wykonaj scoring w kolejności poniżej. Kolejność kategorii jest częścią procedury — nie przestawiaj jej „dla wygody”.',
      },
      {
        type: 'step-list',
        steps: [
          'Przy Koszmarach wykonaj ostatnie sprawdzenie Szaleństwa.',
          'Zdobądź punkty z pola reputacji.',
          'Przyznaj premie za pierwsze i drugie miejsce reputacji: opublikowane rozprawy i wyleczeni pacjenci.',
          'Punktuj Wglądy w kolorze gracza w różnych dzielnicach.',
          'Punktuj wypełnione rzędy notatnika.',
          'Punktuj różne symbole lokacji.',
          'Punktuj pacjentów przełomowych i tajemniczych.',
          'Punktuj świetne Pomysły (niewykorzystane dają po 1 punkcie).',
          'Dodaj wszystkie efekty końcowe z komponentów.',
          'Rozstrzygnij remis.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Z Koszmarami najpierw robicie sprawdzenie Szaleństwa, potem punkty z pola reputacji i resztę listy. Nie zaczynajcie od świetnych Pomysłów „bo łatwiej policzyć”. Niewykorzystane świetne Pomysły dodajecie jako po 1 punkcie dopiero we właściwym kroku.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijanie końcowego sprawdzenia Szaleństwa przy Koszmarach; punktowanie nieweryfikowanych wartości z pamięci.',
      },
      {
        type: 'tip',
        text: '[DO WERYFIKACJI: pełna tabela końcowa i kryterium remisu]. Do domknięcia: sekcja punktowania z polskiej instrukcji. Procedura listy kategorii jest w źródle; dokładne mnożniki/tabela — blocker (nie blokuje nauki przebiegu partii, blokuje niezależne rozstrzygnięcie remisu bez papieru).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najpierw Szaleństwo (jeśli dodatek), potem reputacja i reszta kategorii. P: Czy remis jest opisany tu w pełni? O: Kryterium remisu jest do weryfikacji. P: Czy niewykorzystane świetne Pomysły punktują? O: Tak — po 1 punkcie.',
      },
    ],
  },
  {
    title: '21. FIRST GAME',
    content: [
      {
        type: 'heading',
        text: 'Cel pierwszej partii',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nauczcie trzech opcji tury, kałamarza i jednej terapii bez modułów. Koszmary dopiero potem.',
      },
      {
        type: 'step-list',
        steps: [
          'Graj bez modułów i bez Koszmarów.',
          'Pokaż trzy opcje tury.',
          'W pierwszej turze każdy Ogłasza Pomysły.',
          'Fizycznie przesuń kałamarz i uruchom wskazaną linię.',
          'Następnie pokaż Przywołanie.',
          'Leczenie omów na jednym pacjencie z jawnie policzonymi Wglądami.',
          'Koszmary dodaj dopiero po zrozumieniu terapii i tarczy Wglądu.',
        ],
      },
      {
        type: 'warning',
        text: 'Typowy błąd: start od razu z Koszmarami, pupilami i gramofonem.',
      },
      {
        type: 'tip',
        text: 'Odsyłacze: struktura tury (5), Ogłoś (9), Przywołaj (13), Lecz (14), Koszmary (menu dodatku).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podstawa najpierw; Koszmary po terapii i tarczy. P: Czy pierwsza tura może być Leczeniem? O: First Game każe zacząć od Ogłoszenia. P: Czy pupile w First Game? O: Nie — bez modułów.',
      },
    ],
  },
  {
    title: '22. QUICK REFERENCE',
    content: [
      {
        type: 'paragraph',
        text: 'Tura: Ogłoś / Przywołaj / Lecz → opcjonalne puzderka → obowiązkowe uzupełnienie gabinetów.',
      },
      {
        type: 'paragraph',
        text: 'Ogłoś: umieść 1–2 Pomysły → akcja → kałamarz (obowiązkowy ruch).',
      },
      {
        type: 'paragraph',
        text: 'Wgląd: stwórz / wznieś / stłum / przesuń / użyj (nadpłata bez reszty).',
      },
      {
        type: 'paragraph',
        text: 'Terapia: zapłać marzenie → punkty terapii (max) → Katharsis → wyleczenie.',
      },
      {
        type: 'paragraph',
        text: 'Koniec: Freud na końcu → dokończ rundę → jeszcze jedna runda → scoring.',
      },
      {
        type: 'paragraph',
        text: 'Koszmary: Szaleństwo / spaczenia / sprawdzenia; progi 3 / 5 / 8 / 13 według karty podsumowania.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'To ściąga, nie zastępstwo rozdziałów. P: Gdzie pełna publikacja? O: Rozdział 11. P: Gdzie Szaleństwo? O: Rozdział Koszmary.',
      },
    ],
  },
  {
    title: '23. NAJCZĘSTSZE BŁĘDY',
    content: [
      {
        type: 'paragraph',
        text: 'Poniższa lista to najczęstsze pomyłki przy stole z lokalnego szkicu. Każdy punkt ma pełniejsze omówienie w rozdziale mechaniki, której dotyczy.',
      },
      {
        type: 'step-list',
        steps: [
          'Pomijanie obowiązkowego ruchu kałamarza.',
          'Umieszczanie Pomysłów na dwóch polach w podstawowej grze.',
          'Kierowanie nowego Pomysłu na pole już wskazane przez własny Pomysł.',
          'Używanie karty badań z ręki do publikacji.',
          'Cytowanie własnej rozprawy.',
          'Zatrzymywanie niewykorzystanych punktów terapii.',
          'Zdobywanie punktów pacjenta przy Katharsis zamiast przy wyleczeniu.',
          'Traktowanie puzderka jak punktu terapii przed wydaniem.',
          'Poruszanie szalonego Wglądu standardowymi efektami.',
          'Pomijanie końcowego sprawdzenia Szaleństwa.',
        ],
      },
      {
        type: 'tip',
        text: 'Odsyłacze: kałamarz/Ogłoś (9), publikacja (11), leczenie (14), puzderka (15), Koszmary, punktowanie (20).',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Większość błędów to pominięcie obowiązkowego kroku albo zmieszanie Katharsis z wyleczeniem. P: Czy lista zastępuje ostrzeżenia w rozdziałach? O: Nie — uzupełnia je.',
      },
    ],
  },
  {
    title: '24. FAQ',
    content: [
      {
        type: 'paragraph',
        text: 'Czy mogę wykonać dwie opcje tury? Nie.',
      },
      {
        type: 'paragraph',
        text: 'Czy dwa Pomysły muszą wskazywać to samo pole? W podstawowej grze tak. Koszmary mogą odblokować wyjątek przy odpowiednim poziomie Szaleństwa.',
      },
      {
        type: 'paragraph',
        text: 'Czy kawa może być wydana w trakcie efektu? Tak, w dowolnym momencie własnej tury.',
      },
      {
        type: 'paragraph',
        text: 'Czy wyższy Wgląd może zastąpić niższy? Tak, bez reszty.',
      },
      {
        type: 'paragraph',
        text: 'Czy mogę dobrowolnie nie wykorzystać części terapii? Nie. Musisz wykorzystać maksymalną możliwą liczbę punktów.',
      },
      {
        type: 'paragraph',
        text: 'Kiedy dobieram pacjenta? Na końcu własnej tury, jeśli gabinet jest pusty.',
      },
      {
        type: 'paragraph',
        text: 'Czy Freud uruchamia efekty lokacji przy ruchu na torze reputacji? Nie, jeśli jest przesuwany tylko przez przekroczenie symbolu na torze.',
      },
      {
        type: 'paragraph',
        text: 'Czy Szaleństwo jest wyłącznie karą? Nie. Odblokowuje stałe korzyści, ale powoduje straty w sprawdzeniach.',
      },
    ],
  },
  {
    title: '25. GLOSSARY',
    content: [
      {
        type: 'step-list',
        steps: [
          'Pomysł — znacznik pracownika na stole spotkań.',
          'Kałamarz — znacznik aktywujący rząd lub kolumnę silnika.',
          'Wgląd — zasób o typie i poziomie.',
          'Katharsis — punkt terapii usuwający traumę i aktywujący pełny tekst pacjenta.',
          'Puzderko — zasób zamieniany na punkt terapii.',
          'Świetny Pomysł — uniwersalny znacznik i modyfikator.',
          'Rozprawa — publikacja tworzona z tomów badań.',
          'Szaleństwo — zasób/ryzyko dodatku Koszmary.',
          'Spaczenie — miejsce przechowywania szalonego Wglądu.',
        ],
      },
    ],
  },
];

export const nieswiadomyUmyslFirstGame: FirstGameStep[] = [
  {
    id: 1,
    title: 'Bez modułów',
    content: ['Graj bez modułów i bez Koszmarów.'],
    why: 'Najpierw trzy opcje tury, tarcza i terapia — bez Szaleństwa.',
    commonMistake: 'Włączanie Koszmarów, pupili i gramofonu od pierwszej partii.',
    chapterIndex: 20,
  },
  {
    id: 2,
    title: 'Trzy opcje tury',
    content: ['Pokaż trzy opcje tury: Ogłoś, Przywołaj, Lecz.'],
    why: 'W turze wolno wybrać dokładnie jedną opcję.',
    commonMistake: 'Łączenie dwóch opcji w jednej turze.',
    chapterIndex: 4,
  },
  {
    id: 3,
    title: 'Pierwsze Ogłoszenie',
    content: ['W pierwszej turze każdy Ogłasza Pomysły.'],
    why: 'Ogłoszenie uczy stołu spotkań i akcji pól.',
    commonMistake: 'Start od Leczenia bez zrozumienia Wglądów.',
    chapterIndex: 8,
  },
  {
    id: 4,
    title: 'Kałamarz',
    content: ['Fizycznie przesuń kałamarz i uruchom wskazaną linię.'],
    why: 'Ruch kałamarza jest obowiązkowy po Ogłoszeniu.',
    commonMistake: 'Pomijanie ruchu kałamarza.',
    chapterIndex: 8,
  },
  {
    id: 5,
    title: 'Przywołanie',
    content: ['Następnie pokaż Przywołanie.'],
    why: 'Przywołanie odzyskuje Pomysły i daje premię mapy — to nie Ogłoszenie.',
    commonMistake: 'Myślenie, że Przywołanie kładzie nowe Pomysły na stół.',
    chapterIndex: 12,
  },
  {
    id: 6,
    title: 'Jedno Leczenie',
    content: ['Leczenie omów na jednym pacjencie z jawnie policzonymi Wglądami.'],
    why: 'Widać Katharsis vs wyleczenie i obowiązek max terapii.',
    commonMistake: 'Branie punktów pacjenta przy Katharsis zamiast przy wyleczeniu.',
    chapterIndex: 13,
  },
  {
    id: 7,
    title: 'Potem Koszmary',
    content: ['Koszmary dodaj dopiero po zrozumieniu terapii i tarczy Wglądu.'],
    why: 'Szalone Wglądy i Szaleństwo budują na podstawie.',
    commonMistake: 'Włączanie dodatku przed opanowaniem tarczy.',
    chapterIndex: 16,
    requiredAddons: ['nightmares'],
  },
];
